/*
  Iter122k139 · Credius · Editor modelli Diffide/PDR — Studio Izzo Admin only.
  Permette di modificare (override) i blocchi testuali dei 4 modelli default
  (diffida_interna, diffida_esterna, pdr_interno, pdr_esterno) e mostra la
  legenda dei placeholder disponibili raggruppati per contesto.
*/
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, X, Save, RotateCcw, ChevronDown, ChevronUp, Copy } from "lucide-react";

const DOC_ORDER = ["diffida_interna", "diffida_esterna", "pdr_interno", "pdr_esterno"];

export function DiffideTemplateEditor({ onClose }) {
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState("diffida_interna");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openBlock, setOpenBlock] = useState(0);

  const loadList = () =>
    api.get("/credius/diffide/templates").then((r) => setTemplates(r.data))
      .catch((e) => { toast.error(formatApiError(e) || "Caricamento fallito"); onClose(); });

  const loadDoc = (id) =>
    api.get(`/credius/diffide/templates/${id}`).then((r) => setDoc(r.data))
      .catch((e) => toast.error(formatApiError(e) || "Caricamento modello fallito"));

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selected) loadDoc(selected); /* eslint-disable-next-line */ }, [selected]);

  const patchBlock = (idx, text) => {
    setDoc((d) => ({
      ...d,
      blocchi: d.blocchi.map((b, i) => i === idx ? { ...b, text, is_override: true } : b),
    }));
  };

  const salva = async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const overrides = {};
      doc.blocchi.forEach((b) => {
        if (b.is_override) overrides[b.key] = b.text;
      });
      await api.put(`/credius/diffide/templates/${selected}`, { overrides });
      toast.success("Modello salvato");
      loadList(); loadDoc(selected);
    } catch (e) { toast.error(formatApiError(e) || "Salvataggio fallito"); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare il modello ai valori di default?")) return;
    setBusy(true);
    try {
      await api.delete(`/credius/diffide/templates/${selected}`);
      toast.success("Modello ripristinato");
      loadList(); loadDoc(selected);
    } catch (e) { toast.error(formatApiError(e) || "Reset fallito"); }
    finally { setBusy(false); }
  };

  const copiaPlaceholder = (ph) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(ph);
      toast.success(`${ph} copiato`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-auto"
         data-testid="diffide-template-editor">
      <div className="bg-white w-full max-w-5xl rounded-xl border-2 border-slate-200 shadow-xl">
        <div className="flex items-center justify-between gap-2 p-4 border-b-2 border-slate-100">
          <div>
            <h2 className="font-display font-bold text-lg text-slate-900">
              Editor Modelli Diffide/PDR — Studio Izzo
            </h2>
            <p className="text-xs text-slate-500">
              Le modifiche si applicano ai PDF/DOCX rigenerati per tutte le pratiche.
            </p>
          </div>
          <button onClick={onClose} data-testid="diffide-template-close"
            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!templates ? (
          <div className="flex items-center justify-center p-10">
            <Loader2 className="w-6 h-6 animate-spin text-brand" />
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {DOC_ORDER.map((id) => {
                const t = templates.templates.find((x) => x.doc_type === id);
                if (!t) return null;
                const active = selected === id;
                return (
                  <button key={id} onClick={() => setSelected(id)}
                    data-testid={`diffide-tpl-tab-${id}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 ${active ? "bg-brand text-white border-brand" : "bg-white text-slate-700 border-slate-200 hover:border-brand"}`}>
                    {t.titolo}
                    {t.has_override && <span className="ml-1 text-[10px]">✎</span>}
                  </button>
                );
              })}
            </div>

            {!doc ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-brand" />
              </div>
            ) : (
              <div className="grid lg:grid-cols-3 gap-4">
                {/* Blocchi (sinistra) */}
                <div className="lg:col-span-2 space-y-2">
                  <p className="text-xs text-slate-500">{doc.descrizione}</p>
                  {doc.blocchi.map((b, i) => {
                    const open = openBlock === i;
                    return (
                      <div key={b.key} className="rounded-lg border-2 border-slate-200 bg-slate-50"
                           data-testid={`diffide-tpl-block-${b.key}`}>
                        <button type="button" onClick={() => setOpenBlock(open ? -1 : i)}
                          className="w-full flex items-center gap-2 p-2 text-left">
                          {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                          <span className="font-mono text-[10px] bg-white border border-slate-300 px-1.5 py-0.5 rounded">
                            {b.key}
                          </span>
                          <span className="font-mono text-[10px] text-slate-500">{b.kind}</span>
                          <span className="text-xs text-slate-700 truncate flex-1">
                            {b.text.replace(/<[^>]+>/g, "").slice(0, 90)}
                          </span>
                          {b.is_override && (
                            <span className="text-[10px] font-bold text-emerald-700">modificato</span>
                          )}
                        </button>
                        {open && (
                          <div className="p-2 pt-0">
                            <Textarea rows={5} value={b.text}
                              data-testid={`diffide-tpl-text-${b.key}`}
                              onChange={(e) => patchBlock(i, e.target.value)}
                              className="font-mono text-xs" />
                            <p className="text-[10px] text-slate-500 mt-1">
                              Tag consentiti: <code>&lt;b&gt;...&lt;/b&gt;</code>. Le graffe <code>{"{...}"}</code> vengono sostituite con i dati reali.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Placeholder legend (destra) */}
                <div className="space-y-2">
                  <div className="rounded-lg border-2 border-slate-200 bg-white p-2">
                    <Label className="text-[11px] uppercase text-slate-500">Placeholder disponibili</Label>
                    <p className="text-[10px] text-slate-400 mb-1">Clicca per copiarli.</p>
                    <div className="space-y-2 max-h-[420px] overflow-auto">
                      {Object.entries(doc.placeholders || {}).map(([gruppo, elenco]) => (
                        <div key={gruppo}>
                          <div className="text-[10px] font-bold uppercase text-brand mb-0.5">{gruppo}</div>
                          <div className="flex flex-wrap gap-1">
                            {elenco.map(([ph, lbl]) => (
                              <button key={ph} onClick={() => copiaPlaceholder(ph)}
                                title={lbl}
                                data-testid={`diffide-tpl-ph-${ph}`}
                                className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 hover:bg-brand hover:text-white hover:border-brand">
                                <Copy className="w-2.5 h-2.5" />{ph}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 p-4 border-t-2 border-slate-100 bg-slate-50 rounded-b-xl">
          <Button variant="outline" onClick={reset} disabled={busy || !doc}
            data-testid="diffide-tpl-reset"
            className="border-slate-300 text-slate-700 hover:bg-slate-100">
            <RotateCcw className="w-4 h-4 mr-1" /> Ripristina default
          </Button>
          <Button onClick={salva} disabled={busy || !doc}
            data-testid="diffide-tpl-save"
            className="bg-brand hover:bg-brand-dark">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Salva modello
          </Button>
        </div>
      </div>
    </div>
  );
}
